<script setup>
const { fetchPage, fetchMedias } = useFetchAPI();

const { data: visitData } = await fetchPage("visit");
const { data: images } = await fetchMedias(
    [visitData.value?.meta_box?.backgroundImage?.[0]],
    "visitUs",
);

const emit = defineEmits(["close"]);

const handleClose = () => {
    emit("close");
};
useClickOutsideClose(".dialogue-content", { onClose: () => emit("close") });

const textRef = ref(null);

onMounted(async () => {
    await nextTick();
    textRef.value.animate();
});
</script>

<template>
    <BaseDialogue @close="handleClose">
        <div
            class="dialogue-content max-width flex h-fit flex-col items-center rounded-3xl bg-base-white lg:flex-row-reverse"
        >
            <img
                :src="images?.[0]?.source_url"
                :alt="images?.[0]?.alt_text"
                loading="lazy"
                class="h-[237.73px] w-full rounded-3xl object-cover object-top sm:h-[271px] lg:h-auto lg:w-[29.62vw] lg:max-w-[524px] lg:self-stretch lg:object-[70%_50%]"
            />

            <div class="w-full px-5 pb-4 pt-6 sm:p-6 lg:px-12 lg:py-10">
                <BaseFormatText2
                    ref="textRef"
                    type="h3"
                    :text="visitData?.meta_box?.title"
                    class="slt-h3 mb-2 whitespace-pre-wrap text-center text-base-black lg:text-left"
                />
                <p
                    class="mb-5 whitespace-pre-wrap text-center text-[16px] font-normal text-base-black-80 sm:mb-4 lg:mb-6 lg:text-left"
                >
                    <span
                        v-for="(item, index) in visitData?.meta_box
                            ?.customContent"
                        :key="index"
                    >
                        <br v-if="index !== 0" />
                        {{ item }}
                    </span>
                </p>

                <div class="grid w-full grid-cols-2 gap-3">
                    <!-- Location -->
                    <div
                        class="dialogue-card col-span-2 flex flex-col gap-6 lg:flex-row lg:items-end lg:gap-5"
                    >
                        <div class="lg:max-w-[209px]">
                            <BaseFormatText
                                type="h3"
                                :text="visitData?.meta_box?.locationInfo?.title"
                                class="dialogue-card-title slt-body-1 bold whitespace-pre-wrap lg:pb-1"
                            />
                            <BaseFormatText
                                type="h6"
                                :text="visitData?.meta_box?.locationInfo?.name"
                                class="slt-h6 mb-3 whitespace-pre-wrap"
                            />
                            <BaseFormatText
                                type="p"
                                :text="
                                    visitData?.meta_box?.locationInfo?.address
                                "
                                class="slt-body-3-d whitespace-pre-wrap font-bold text-base-black-60"
                            />
                        </div>
                        <BaseIconButton
                            variant="primary"
                            class="lg:flex-1"
                            :ariaLabel="$t('ariaLabel.visitUs.location')"
                            :to="
                                $localePath(
                                    visitData?.meta_box?.locationInfo?.button
                                        ?.link,
                                )
                            "
                            @click="handleClose"
                            v-sendGAsignal="{
                                event_category:
                                    visitData?.meta_box?.locationInfo?.button
                                        ?.category,
                                event_label:
                                    visitData?.meta_box?.locationInfo?.button
                                        ?.label,
                            }"
                        >
                            <template #svg
                                ><SvgLocation24
                                    class="h-[6.1vw] w-[6.1vw] xs:h-6 xs:w-6"
                            /></template>
                            <BaseFormatText
                                type="p"
                                :text="
                                    visitData?.meta_box?.locationInfo?.button
                                        ?.text
                                "
                            />
                        </BaseIconButton>
                    </div>

                    <!-- Visitor -->
                    <div class="dialogue-card">
                        <BaseFormatText
                            type="h3"
                            :text="visitData?.meta_box?.targetInfo?.title"
                            class="dialogue-card-title slt-body-1 bold whitespace-pre-wrap"
                        />
                        <ul class="slt-body-3 flex flex-col gap-3">
                            <li
                                v-for="(item, index) in visitData?.meta_box
                                    ?.targetInfo?.list"
                                :key="`target-${index}`"
                                class="flex items-center font-bold"
                            >
                                <span class="mr-2 self-start pt-1">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="8"
                                        height="9"
                                        viewBox="0 0 8 9"
                                        fill="none"
                                    >
                                        <circle
                                            cx="4"
                                            cy="4.5"
                                            r="4"
                                            fill="#FF6400"
                                        />
                                    </svg>
                                </span>
                                <BaseFormatText
                                    type="p"
                                    class="whitespace-pre-wrap"
                                    :text="item.listitem"
                                />
                            </li>
                        </ul>
                    </div>

                    <!-- Limit -->
                    <div class="dialogue-card">
                        <BaseFormatText
                            type="h3"
                            :text="visitData?.meta_box?.capacityInfo?.title"
                            class="dialogue-card-title slt-body-1 bold whitespace-pre-wrap"
                        />

                        <p class="slt-body-3 font-bold">
                            <span class="mr-1 text-[32px]" lang="en">{{
                                visitData?.meta_box?.capacityInfo?.limit
                            }}</span>
                            <BaseFormatText
                                type="span"
                                :text="visitData?.meta_box?.capacityInfo?.text"
                                class="whitespace-pre-wrap align-[0.3rem]"
                            />
                        </p>
                    </div>

                    <div
                        class="dialogue-card col-span-2 flex items-center gap-6 !p-[5.09vw] xs:!p-5"
                    >
                        <BaseFormatText
                            type="h3"
                            :text="$t('visitUs.mailBox')"
                            class="dialogue-card-title slt-body-1 bold !mb-0 whitespace-pre-wrap"
                        />

                        <p
                            class="slt-body-1 tracking-[-0.16px] text-base-black-80"
                        >
                            <BaseFormatText
                                type="span"
                                class="whitespace-pre-wrap"
                                :text="'pr@gamania.com'"
                            />
                        </p>
                    </div>

                    <BaseIconButton
                        variant="secondary"
                        :to="visitData?.meta_box?.mailText?.mail"
                        :ariaLabel="$t('ariaLabel.visitUs.back')"
                        class="col-span-2 mt-1 lg:mt-3 lg:w-[16.5vw] lg:max-w-[320px]"
                        @click="handleClose"
                        v-sendGAsignal="{
                            event_category: 'visitus',
                            event_label: '返回',
                        }"
                    >
                        <template #svg
                            ><SvgArrowBackAngle24
                                class="h-[6.1vw] w-[6.1vw] xs:h-6 xs:w-6"
                        /></template>
                        <BaseFormatText
                            type="p"
                            class="whitespace-pre-wrap"
                            :text="visitData?.meta_box?.mailText?.back"
                        />
                    </BaseIconButton>
                </div>
            </div>
        </div>
    </BaseDialogue>
</template>

<style scoped>
.dialogue-card {
    @apply rounded-2xl bg-base-bgGrey p-[4.07vw] xs:p-4 lg:p-5;
}

.dialogue-card-title {
    @apply mb-3 font-bold text-primary-orange;
}

.max-width {
    @apply max-w-[90.83vw] sm:max-w-[58.85vw] lg:max-w-[69.31vw];
}

@media (min-width: 1024px) {
    :deep(._button) {
        p {
            font-size: 17px !important;
            padding-right: 0 !important;
        }
    }
    :deep(._svg) {
        width: 24px !important;
        height: 24px !important;
    }
}
</style>
